import React, { useState, useEffect } from 'react';
import apiWithAuth from '../../services/apiWithAuth';
import announcementApi from '../announcements/api';

const COODashboard = () => {
    const [stats, setStats] = useState({
        heldApplications: 0,
        rejectedApplications: 0,
        incomingApplications: 0,
        acceptedApplications: 0
    });
    const [announcements, setAnnouncements] = useState([]);
    const [serverStatus, setServerStatus] = useState({ status: 'checking', responseTime: null });
    const [websiteStatus, setWebsiteStatus] = useState({ status: 'checking', responseTime: null });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch application statistics
    const fetchApplicationStats = async () => {
        try {
            const [heldRes, rejectedRes, incomingRes, acceptedRes] = await Promise.all([
                apiWithAuth.get('/users/onboarding-docs/status/hold'),
                apiWithAuth.get('/users/onboarding-docs/status/rejected'),
                apiWithAuth.get('/users/onboarding-docs'),
                apiWithAuth.get('/users/onboarding-docs/status/accepted')
            ]);

            const incomingUsers = Array.isArray(incomingRes.data) 
                ? incomingRes.data.filter(user => user.status === null || user.status === 'pending') 
                : [];

            setStats({
                heldApplications: heldRes.data?.length || 0,
                rejectedApplications: rejectedRes.data?.length || 0,
                incomingApplications: incomingUsers.length,
                acceptedApplications: acceptedRes.data?.length || 0
            });
        } catch (err) {
            console.error('Error fetching application stats:', err);
            setError('Failed to fetch application statistics');
        }
    };

    // Fetch announcements
    const fetchAnnouncements = async () => {
        try {
            // First get all shops
            const shopsResponse = await announcementApi.getShops();
            const shops = shopsResponse.shops || shopsResponse || [];
            
            // Fetch announcements for all shops
            const allAnnouncements = [];
            for (const shop of shops.slice(0, 5)) { // Limit to first 5 shops to avoid too many requests
                try {
                    const response = await announcementApi.getAnnouncements(shop._id || shop.id);
                    const shopAnnouncements = response.announcements || response || [];
                    allAnnouncements.push(...shopAnnouncements);
                } catch (err) {
                    console.error(`Error fetching announcements for shop ${shop._id}:`, err);
                }
            }
            
            // Sort by date and get latest 5
            const sortedAnnouncements = allAnnouncements
                .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                .slice(0, 5);
            
            setAnnouncements(sortedAnnouncements);
        } catch (err) {
            console.error('Error fetching announcements:', err);
        }
    };

    // Check server status
    const checkServerStatus = async () => {
        const startTime = Date.now();
        try {
            const response = await fetch('https://prod.kaamdhanda.co.in/health', {
                method: 'GET',
                mode: 'no-cors'
            });
            const responseTime = Date.now() - startTime;
            setServerStatus({ status: 'up', responseTime });
        } catch (err) {
            setServerStatus({ status: 'down', responseTime: null });
        }
    };

    // Check website status
    const checkWebsiteStatus = async () => {
        const startTime = Date.now();
        try {
            const response = await fetch('https://www.kaamdhanda.co.in/', {
                method: 'GET',
                mode: 'no-cors'
            });
            const responseTime = Date.now() - startTime;
            setWebsiteStatus({ status: 'up', responseTime });
        } catch (err) {
            setWebsiteStatus({ status: 'down', responseTime: null });
        }
    };

    useEffect(() => {
        const loadDashboard = async () => {
            setLoading(true);
            await Promise.all([
                fetchApplicationStats(),
                fetchAnnouncements(),
                checkServerStatus(),
                checkWebsiteStatus()
            ]);
            setLoading(false);
        };

        loadDashboard();

        // Refresh status every 30 seconds
        const statusInterval = setInterval(() => {
            checkServerStatus();
            checkWebsiteStatus();
        }, 30000);

        return () => clearInterval(statusInterval);
    }, []);

    const getStatusColor = (status) => {
        if (status === 'up') return 'bg-green-500';
        if (status === 'down') return 'bg-red-500';
        return 'bg-yellow-500';
    };

    const getStatusText = (status) => {
        if (status === 'up') return 'Online';
        if (status === 'down') return 'Offline';
        return 'Checking...';
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <div className="text-xl text-gray-600">Loading COO Dashboard...</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6 lg:p-8">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-3 rounded-lg shadow-lg">
                            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                        </div>
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">COO Dashboard</h1>
                            <p className="text-gray-600 mt-1">System Overview and Key Metrics</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-500 mt-3">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>Last updated: {new Date().toLocaleString()}</span>
                    </div>
                </div>

                {error && (
                    <div className="bg-red-50 border-l-4 border-red-500 text-red-700 px-4 py-3 rounded-r mb-6 shadow-sm">
                        <div className="flex items-center">
                            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                            {error}
                        </div>
                    </div>
                )}

                {/* Application Statistics */}
                <div className="mb-6">
                    <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Application Statistics
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white transform transition hover:scale-105">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-blue-100 text-sm font-medium">Incoming</p>
                                    <p className="text-4xl font-bold mt-2">{stats.incomingApplications}</p>
                                    <p className="text-blue-100 text-xs mt-1">Pending Review</p>
                                </div>
                               
                            </div>
                        </div>

                        <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl shadow-lg p-6 text-white transform transition hover:scale-105">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-yellow-100 text-sm font-medium">On Hold</p>
                                    <p className="text-4xl font-bold mt-2">{stats.heldApplications}</p>
                                    <p className="text-yellow-100 text-xs mt-1">Awaiting Action</p>
                                </div>
                            
                            </div>
                        </div>

                        <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl shadow-lg p-6 text-white transform transition hover:scale-105">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-red-100 text-sm font-medium">Rejected</p>
                                    <p className="text-4xl font-bold mt-2">{stats.rejectedApplications}</p>
                                    <p className="text-red-100 text-xs mt-1">Not Approved</p>
                                </div>
                               
                            </div>
                        </div>

                        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-white transform transition hover:scale-105">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-green-100 text-sm font-medium">Accepted</p>
                                    <p className="text-4xl font-bold mt-2">{stats.acceptedApplications}</p>
                                    <p className="text-green-100 text-xs mt-1">Approved</p>
                                </div>
                                
                            </div>
                        </div>
                    </div>
                </div>

                {/* System Status */}
                <div className="mb-6">
                    <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                        <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                        System Health
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                        <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-indigo-500">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                                    <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
                                    </svg>
                                    Production Server
                                </h3>
                                <div className={`w-3 h-3 rounded-full ${getStatusColor(serverStatus.status)}`}></div>
                            </div>
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-gray-600">URL:</span>
                                    <span className="text-sm font-mono text-gray-800">prod.kaamdhanda.co.in</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-gray-600">Status:</span>
                                    <span className={`text-sm font-semibold ${serverStatus.status === 'up' ? 'text-green-600' : serverStatus.status === 'down' ? 'text-red-600' : 'text-yellow-600'}`}>
                                        {getStatusText(serverStatus.status)}
                                    </span>
                                </div>
                                {serverStatus.responseTime && (
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-gray-600">Response Time:</span>
                                        <span className="text-sm font-semibold text-gray-800">{serverStatus.responseTime}ms</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-purple-500">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                                    <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                                    </svg>
                                    Public Website
                                </h3>
                                <div className={`w-3 h-3 rounded-full ${getStatusColor(websiteStatus.status)}`}></div>
                            </div>
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-gray-600">URL:</span>
                                    <span className="text-sm font-mono text-gray-800">www.kaamdhanda.co.in</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-gray-600">Status:</span>
                                    <span className={`text-sm font-semibold ${websiteStatus.status === 'up' ? 'text-green-600' : websiteStatus.status === 'down' ? 'text-red-600' : 'text-yellow-600'}`}>
                                        {getStatusText(websiteStatus.status)}
                                    </span>
                                </div>
                                {websiteStatus.responseTime && (
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-gray-600">Response Time:</span>
                                        <span className="text-sm font-semibold text-gray-800">{websiteStatus.responseTime}ms</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Recent Announcements */}
                <div className="bg-white rounded-xl shadow-lg p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                            <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                            </svg>
                            Recent Announcements
                        </h2>
                        <span className="text-sm text-gray-500">{announcements.length} total</span>
                    </div>
                    {announcements.length === 0 ? (
                        <div className="text-center py-12">
                            <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                            </svg>
                            <p className="text-gray-500 text-lg">No announcements available</p>
                            <p className="text-gray-400 text-sm mt-1">Check back later for updates</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {announcements.slice(0, 5).map((announcement, index) => (
                                <div key={announcement._id || index} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow bg-gradient-to-r from-gray-50 to-white">
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-2">
                                                <h3 className="font-semibold text-gray-900 text-lg">{announcement.title}</h3>
                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${announcement.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                                                    {announcement.isActive ? '● Active' : '○ Inactive'}
                                                </span>
                                            </div>
                                            <p className="text-sm text-gray-600 mb-3 line-clamp-2">{announcement.description || announcement.message || 'No description available'}</p>
                                            <div className="flex items-center gap-4 text-xs text-gray-500">
                                                <span className="flex items-center gap-1">
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                                    </svg>
                                                    {announcement.type || 'General'}
                                                </span>
                                                {announcement.createdAt && (
                                                    <span className="flex items-center gap-1">
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                        </svg>
                                                        {new Date(announcement.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                                    </span>
                                                )}
                                                {announcement.shopName && (
                                                    <span className="flex items-center gap-1">
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                                        </svg>
                                                        {announcement.shopName}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default COODashboard;
